/**
 * Angular service for User Files API operations
 * 
 * @remarks
 * Provides comprehensive methods for managing user-uploaded files including:
 * - Single and batch file creation
 * - File retrieval with filtering and pagination
 * - Organization and user-scoped queries
 * - Search functionality by file name
 * - File type filtering
 * - Statistics and analytics
 * - Soft delete and restore (admin)
 * 
 * All methods return RxJS Observables for reactive programming patterns.
 * Authentication is handled by HTTP interceptors in the consuming application.
 * 
 * @example
 * ```typescript
 * // In your module or standalone component
 * import { provideUserFilesService } from 'cms-post-lib';
 * 
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     provideUserFilesService({
 *       apiBaseUrl: environment.apiBaseUrl
 *     })
 *   ]
 * };
 * 
 * // In your component
 * constructor(private filesService: UserFilesService) {}
 * 
 * ngOnInit() {
 *   this.filesService.getAllFiles({ orgId: '507f1f77bcf86cd799439011' })
 *     .subscribe(response => {
 *       this.files = response.data.files;
 *     });
 * }
 * ```
 * 
 * @see USER_FILES_API_DOCUMENTATION.md for complete API documentation
 * @see USER_FILES_QUICK_REFERENCE.md for quick reference guide
 */

import { Injectable, inject, InjectionToken } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  UserFilePopulated,
  CreateUserFileDto,
  BatchCreateUserFileDto,
  UpdateUserFileDto,
  GetAllFilesQueryParams,
  GetFilesByOrganizationQueryParams,
  GetFilesByUserQueryParams,
  SearchFilesQueryParams,
  GetFilesByTypeQueryParams,
  CreateFileResponse,
  BatchCreateFileResponse,
  FileListResponse,
  GetFileResponse,
  UpdateFileResponse,
  DeleteFileResponse,
  RestoreFileResponse,
  FileStatsResponse
} from '../models/user-file.model';

/**
 * Configuration interface for UserFiles service
 */
export interface UserFilesConfig {
  /**
   * Base URL for the API (without trailing slash)
   * @example 'https://api.example.com'
   */
  apiBaseUrl: string;

  /**
   * Enable debug logging
   * @default false
   */
  debug?: boolean;
}

/**
 * Injection token for UserFiles service configuration
 */
export const USER_FILES_API_CONFIG = new InjectionToken<UserFilesConfig>(
  'USER_FILES_API_CONFIG',
  {
    providedIn: 'root',
    factory: () => ({
      apiBaseUrl: '',
      debug: false
    })
  }
);

/**
 * User Files API Service
 * 
 * @remarks
 * Manages all user file operations including various document types.
 * All files are scoped to organizations with automatic access control.
 */
@Injectable({
  providedIn: 'root'
})
export class UserFilesService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(USER_FILES_API_CONFIG);
  private readonly baseUrl = `${this.config.apiBaseUrl}/api/user-files`;

  /**
   * Log debug messages if debug mode is enabled
   */
  private log(message: string, ...args: any[]): void {
    if (this.config.debug) {
      console.log(`[UserFilesService] ${message}`, ...args);
    }
  }

  /**
   * Create a single file record
   * 
   * @remarks
   * Creates a new file record with URL and storage path.
   * Current user must belong to the specified organization.
   * User is automatically set as the uploader from auth token.
   * 
   * @param dto - File creation data
   * @returns Observable of created file with populated references
   * 
   * @example
   * ```typescript
   * const newFile: CreateUserFileDto = {
   *   fileName: 'Invoice_2024_001.pdf',
   *   orgId: '507f1f77bcf86cd799439011',
   *   urls: 'https://storage.googleapis.com/bucket/invoice.pdf',
   *   storagePath: 'files/user123/invoices/invoice_001.pdf',
   *   fileType: 'application/pdf'
   * };
   * 
   * this.filesService.createFile(newFile).subscribe(
   *   response => console.log('File created:', response.data.file)
   * );
   * ```
   */
  createFile(dto: CreateUserFileDto): Observable<CreateFileResponse> {
    this.log('Creating file', dto);
    return this.http.post<CreateFileResponse>(this.baseUrl, dto).pipe(
      tap(response => this.log('File created successfully', response.data.file))
    );
  }

  /**
   * Create multiple file records in a single request
   * 
   * @remarks
   * Batch creates file records. User must have access to all specified organizations.
   * Minimum 1 file required in the array.
   * 
   * @param dto - Batch file creation data
   * @returns Observable of created files with count
   * 
   * @example
   * ```typescript
   * const batchData: BatchCreateUserFileDto = {
   *   files: [
   *     {
   *       fileName: 'Receipt1.jpg',
   *       orgId: '507f1f77bcf86cd799439011',
   *       urls: 'https://storage.googleapis.com/bucket/receipt1.jpg',
   *       storagePath: 'files/user123/receipts/receipt1.jpg',
   *       fileType: 'image/jpeg'
   *     },
   *     {
   *       fileName: 'Receipt2.jpg',
   *       orgId: '507f1f77bcf86cd799439011',
   *       urls: 'https://storage.googleapis.com/bucket/receipt2.jpg',
   *       storagePath: 'files/user123/receipts/receipt2.jpg',
   *       fileType: 'image/jpeg'
   *     }
   *   ]
   * };
   * 
   * this.filesService.batchCreateFiles(batchData).subscribe(
   *   response => {
   *     console.log(`${response.data.count} files created`);
   *     console.log('Files:', response.data.files);
   *   }
   * );
   * ```
   */
  batchCreateFiles(dto: BatchCreateUserFileDto): Observable<BatchCreateFileResponse> {
    this.log('Batch creating files', dto);
    return this.http.post<BatchCreateFileResponse>(`${this.baseUrl}/batch`, dto).pipe(
      tap(response => this.log(`${response.data.count} files created successfully`))
    );
  }

  /**
   * Get all files with filtering and pagination
   * 
   * @remarks
   * Returns files from user's organizations only.
   * Excludes deleted files by default.
   * Results sorted by creation date (newest first).
   * 
   * @param params - Query parameters for filtering and pagination
   * @returns Observable of file list with pagination
   * 
   * @example
   * ```typescript
   * // Get all files with pagination
   * this.filesService.getAllFiles({
   *   page: 1,
   *   limit: 10
   * }).subscribe(response => {
   *   console.log('Files:', response.data.files);
   *   console.log('Total pages:', response.data.pagination.pages);
   * });
   * 
   * // Filter by organization and file type
   * this.filesService.getAllFiles({
   *   orgId: '507f1f77bcf86cd799439011',
   *   fileType: 'application/pdf',
   *   page: 1,
   *   limit: 20
   * }).subscribe(response => {
   *   console.log('Filtered files:', response.data.files);
   * });
   * ```
   */
  getAllFiles(params: GetAllFilesQueryParams = {}): Observable<FileListResponse> {
    this.log('Getting all files', params);

    let httpParams = new HttpParams();

    if (params.page !== undefined) {
      httpParams = httpParams.set('page', params.page.toString());
    }

    if (params.limit !== undefined) {
      httpParams = httpParams.set('limit', params.limit.toString());
    }

    if (params.fileName) {
      httpParams = httpParams.set('fileName', params.fileName);
    }

    if (params.fileType) {
      httpParams = httpParams.set('fileType', params.fileType);
    }

    if (params.orgId) {
      httpParams = httpParams.set('orgId', params.orgId);
    }

    if (params.startDate) {
      httpParams = httpParams.set('startDate', params.startDate);
    }

    if (params.endDate) {
      httpParams = httpParams.set('endDate', params.endDate);
    }

    return this.http.get<FileListResponse>(this.baseUrl, { params: httpParams }).pipe(
      tap(response => this.log(`Retrieved ${response.data.files.length} files`, response.data.pagination))
    );
  }

  /**
   * Get a specific file by ID
   * 
   * @remarks
   * File must belong to one of user's organizations.
   * 
   * @param fileId - MongoDB ObjectId of the file
   * @returns Observable of file with populated references
   * 
   * @example
   * ```typescript
   * this.filesService.getFileById('507f191e810c19729de860ea')
   *   .subscribe(response => {
   *     console.log('File:', response.data.file);
   *     console.log('File name:', response.data.file.fileName);
   *     console.log('URL:', response.data.file.urls);
   *   });
   * ```
   */
  getFileById(fileId: string): Observable<GetFileResponse> {
    this.log('Getting file by ID', fileId);
    return this.http.get<GetFileResponse>(`${this.baseUrl}/${fileId}`).pipe(
      tap(response => this.log('Retrieved file', response.data.file))
    );
  }

  /**
   * Get files for a specific organization
   * 
   * @remarks
   * User must belong to the organization to access its files.
   * 
   * @param orgId - MongoDB ObjectId of the organization
   * @param params - Query parameters for pagination
   * @returns Observable of file list with pagination
   * 
   * @example
   * ```typescript
   * this.filesService.getFilesByOrganization('507f1f77bcf86cd799439011', {
   *   page: 1,
   *   limit: 10
   * }).subscribe(response => {
   *   console.log('Organization files:', response.data.files);
   * });
   * ```
   */
  getFilesByOrganization(
    orgId: string,
    params: GetFilesByOrganizationQueryParams = {}
  ): Observable<FileListResponse> {
    this.log('Getting files for organization', orgId, params);

    let httpParams = new HttpParams();

    if (params.page !== undefined) {
      httpParams = httpParams.set('page', params.page.toString());
    }

    if (params.limit !== undefined) {
      httpParams = httpParams.set('limit', params.limit.toString());
    }

    return this.http.get<FileListResponse>(`${this.baseUrl}/organization/${orgId}`, { params: httpParams }).pipe(
      tap(response => this.log(`Retrieved ${response.data.files.length} files for organization`, response.data.pagination))
    );
  }

  /**
   * Get files uploaded by a specific user
   * 
   * @remarks
   * User can view their own files, or admin can view any user's files.
   * 
   * @param userId - MongoDB ObjectId of the user
   * @param params - Query parameters for pagination
   * @returns Observable of file list with pagination
   * 
   * @example
   * ```typescript
   * this.filesService.getFilesByUser('507f191e810c19729de860ea', {
   *   page: 1,
   *   limit: 10
   * }).subscribe(response => {
   *   console.log('User files:', response.data.files);
   * });
   * ```
   */
  getFilesByUser(
    userId: string,
    params: GetFilesByUserQueryParams = {}
  ): Observable<FileListResponse> {
    this.log('Getting files for user', userId, params);

    let httpParams = new HttpParams();

    if (params.page !== undefined) {
      httpParams = httpParams.set('page', params.page.toString());
    }

    if (params.limit !== undefined) {
      httpParams = httpParams.set('limit', params.limit.toString());
    }

    return this.http.get<FileListResponse>(`${this.baseUrl}/user/${userId}`, { params: httpParams }).pipe(
      tap(response => this.log(`Retrieved ${response.data.files.length} files for user`, response.data.pagination))
    );
  }

  /**
   * Search files by file name
   * 
   * @remarks
   * Searches within a specific organization.
   * User must belong to the organization.
   * 
   * @param params - Search query and filters
   * @returns Observable of matching files with pagination
   * 
   * @example
   * ```typescript
   * this.filesService.searchFiles({
   *   q: 'invoice',
   *   orgId: '507f1f77bcf86cd799439011',
   *   page: 1,
   *   limit: 10
   * }).subscribe(response => {
   *   console.log('Search results:', response.data.files);
   * });
   * ```
   */
  searchFiles(params: SearchFilesQueryParams): Observable<FileListResponse> {
    this.log('Searching files', params);

    let httpParams = new HttpParams()
      .set('q', params.q)
      .set('orgId', params.orgId);

    if (params.page !== undefined) {
      httpParams = httpParams.set('page', params.page.toString());
    }

    if (params.limit !== undefined) {
      httpParams = httpParams.set('limit', params.limit.toString());
    }

    return this.http.get<FileListResponse>(`${this.baseUrl}/search`, { params: httpParams }).pipe(
      tap(response => this.log(`Found ${response.data.files.length} matching files`, response.data.pagination))
    );
  }

  /**
   * Get files by MIME type
   * 
   * @remarks
   * Filter files by their MIME type within an organization.
   * Supports partial matching (e.g., 'image' matches all image types).
   * User must belong to the organization.
   * 
   * @param fileType - MIME type to filter by (can be partial, e.g., 'application/pdf' or 'image')
   * @param params - Query parameters including orgId and pagination
   * @returns Observable of matching files with pagination
   * 
   * @example
   * ```typescript
   * // Get all PDFs
   * this.filesService.getFilesByType('application/pdf', {
   *   orgId: '507f1f77bcf86cd799439011',
   *   page: 1,
   *   limit: 10
   * }).subscribe(response => {
   *   console.log('PDF files:', response.data.files);
   * });
   * 
   * // Get all images
   * this.filesService.getFilesByType('image', {
   *   orgId: '507f1f77bcf86cd799439011'
   * }).subscribe(response => {
   *   console.log('Image files:', response.data.files);
   * });
   * ```
   */
  getFilesByType(
    fileType: string,
    params: GetFilesByTypeQueryParams
  ): Observable<FileListResponse> {
    this.log('Getting files by type', fileType, params);

    let httpParams = new HttpParams()
      .set('orgId', params.orgId);

    if (params.page !== undefined) {
      httpParams = httpParams.set('page', params.page.toString());
    }

    if (params.limit !== undefined) {
      httpParams = httpParams.set('limit', params.limit.toString());
    }

    return this.http.get<FileListResponse>(`${this.baseUrl}/type/${encodeURIComponent(fileType)}`, { params: httpParams }).pipe(
      tap(response => this.log(`Retrieved ${response.data.files.length} files of type ${fileType}`, response.data.pagination))
    );
  }

  /**
   * Get file statistics for an organization
   * 
   * @remarks
   * Returns aggregate statistics including total counts, user breakdown,
   * file type distribution, and recent activity.
   * User must belong to the organization.
   * 
   * @param orgId - MongoDB ObjectId of the organization
   * @returns Observable of file statistics
   * 
   * @example
   * ```typescript
   * this.filesService.getFileStats('507f1f77bcf86cd799439011')
   *   .subscribe(response => {
   *     const stats = response.data.stats;
   *     console.log('Total files:', stats.totalFiles);
   *     console.log('Active files:', stats.activeFiles);
   *     console.log('Recent uploads:', stats.recentUploads);
   *     console.log('Files by type:', stats.filesByType);
   *     console.log('Files by user:', stats.filesByUser);
   *   });
   * ```
   */
  getFileStats(orgId: string): Observable<FileStatsResponse> {
    this.log('Getting file statistics for organization', orgId);
    return this.http.get<FileStatsResponse>(`${this.baseUrl}/stats/${orgId}`).pipe(
      tap(response => this.log('Retrieved file statistics', response.data.stats))
    );
  }

  /**
   * Update a file record
   * 
   * @remarks
   * Can update file name, URLs, storage path, and/or file type.
   * File must belong to user's organizations.
   * All fields are optional.
   * 
   * @param fileId - MongoDB ObjectId of the file
   * @param dto - Updated file data
   * @returns Observable of updated file
   * 
   * @example
   * ```typescript
   * const updates: UpdateUserFileDto = {
   *   fileName: 'Updated_Invoice.pdf',
   *   urls: 'https://storage.googleapis.com/bucket/new-invoice.pdf'
   * };
   * 
   * this.filesService.updateFile('507f191e810c19729de860ea', updates)
   *   .subscribe(response => {
   *     console.log('File updated:', response.data.file);
   *   });
   * ```
   */
  updateFile(fileId: string, dto: UpdateUserFileDto): Observable<UpdateFileResponse> {
    this.log('Updating file', fileId, dto);
    return this.http.put<UpdateFileResponse>(`${this.baseUrl}/${fileId}`, dto).pipe(
      tap(response => this.log('File updated successfully', response.data.file))
    );
  }

  /**
   * Delete a file record (soft delete)
   * 
   * @remarks
   * Performs soft delete: sets isDeleted: true and deletedAt: Date.
   * File record is not physically removed and can be restored by admin.
   * File must belong to user's organizations.
   * 
   * @param fileId - MongoDB ObjectId of the file
   * @returns Observable of deletion confirmation
   * 
   * @example
   * ```typescript
   * this.filesService.deleteFile('507f191e810c19729de860ea')
   *   .subscribe(response => {
   *     console.log(response.message); // "File deleted successfully"
   *   });
   * ```
   */
  deleteFile(fileId: string): Observable<DeleteFileResponse> {
    this.log('Deleting file', fileId);
    return this.http.delete<DeleteFileResponse>(`${this.baseUrl}/${fileId}`).pipe(
      tap(response => this.log('File deleted successfully', response.message))
    );
  }

  /**
   * Restore a deleted file (admin only)
   * 
   * @remarks
   * Undeletes file by setting isDeleted: false and clearing deletedAt.
   * Requires admin role.
   * 
   * @param fileId - MongoDB ObjectId of the file
   * @returns Observable of restored file
   * 
   * @example
   * ```typescript
   * this.filesService.restoreFile('507f191e810c19729de860ea')
   *   .subscribe(response => {
   *     console.log('File restored:', response.data.file);
   *   });
   * ```
   */
  restoreFile(fileId: string): Observable<RestoreFileResponse> {
    this.log('Restoring file', fileId);
    return this.http.post<RestoreFileResponse>(`${this.baseUrl}/${fileId}/restore`, {}).pipe(
      tap(response => this.log('File restored successfully', response.data.file))
    );
  }

  // ============================================================================
  // Convenience Methods
  // ============================================================================

  /**
   * Get files for the current page with default pagination
   * 
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 10)
   * @returns Observable of paginated files
   * 
   * @example
   * ```typescript
   * this.filesService.getFilesPage(2, 20).subscribe(response => {
   *   console.log('Page 2 files:', response.data.files);
   * });
   * ```
   */
  getFilesPage(page = 1, limit = 10): Observable<FileListResponse> {
    this.log('Getting files page', page, 'with limit', limit);
    return this.getAllFiles({ page, limit });
  }

  /**
   * Get files by file name filter
   * 
   * @param fileName - File name to filter by
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 10)
   * @returns Observable of filtered files
   * 
   * @example
   * ```typescript
   * this.filesService.getFilesByFileName('invoice').subscribe(response => {
   *   console.log('Filtered files:', response.data.files);
   * });
   * ```
   */
  getFilesByFileName(fileName: string, page = 1, limit = 10): Observable<FileListResponse> {
    this.log('Getting files by file name', fileName);
    return this.getAllFiles({ fileName, page, limit });
  }

  /**
   * Get files within a date range
   * 
   * @param startDate - Start date (ISO string)
   * @param endDate - End date (ISO string)
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 10)
   * @returns Observable of date-filtered files
   * 
   * @example
   * ```typescript
   * this.filesService.getFilesByDateRange(
   *   '2024-01-01T00:00:00.000Z',
   *   '2024-01-31T23:59:59.999Z'
   * ).subscribe(response => {
   *   console.log('January files:', response.data.files);
   * });
   * ```
   */
  getFilesByDateRange(startDate: string, endDate: string, page = 1, limit = 10): Observable<FileListResponse> {
    this.log('Getting files by date range', { startDate, endDate });
    return this.getAllFiles({ startDate, endDate, page, limit });
  }

  /**
   * Get organization files with default pagination
   * 
   * @param orgId - MongoDB ObjectId of the organization
   * @returns Observable of organization files (first page, 10 items)
   * 
   * @example
   * ```typescript
   * this.filesService.getOrgFiles('507f1f77bcf86cd799439011')
   *   .subscribe(response => {
   *     console.log('Organization files:', response.data.files);
   *   });
   * ```
   */
  getOrgFiles(orgId: string): Observable<FileListResponse> {
    this.log('Getting organization files with defaults', orgId);
    return this.getFilesByOrganization(orgId);
  }

  /**
   * Update file name only
   * 
   * @param fileId - MongoDB ObjectId of the file
   * @param fileName - New file name
   * @returns Observable of updated file
   * 
   * @example
   * ```typescript
   * this.filesService.updateFileName('507f191e810c19729de860ea', 'new-name.pdf')
   *   .subscribe(response => {
   *     console.log('File name updated:', response.data.file.fileName);
   *   });
   * ```
   */
  updateFileName(fileId: string, fileName: string): Observable<UpdateFileResponse> {
    this.log('Updating file name', fileId, fileName);
    return this.updateFile(fileId, { fileName });
  }

  /**
   * Update file URL only
   * 
   * @param fileId - MongoDB ObjectId of the file
   * @param urls - New file URL
   * @returns Observable of updated file
   * 
   * @example
   * ```typescript
   * this.filesService.updateFileUrl(
   *   '507f191e810c19729de860ea',
   *   'https://storage.googleapis.com/bucket/new-file.pdf'
   * ).subscribe(response => {
   *   console.log('URL updated:', response.data.file.urls);
   * });
   * ```
   */
  updateFileUrl(fileId: string, urls: string): Observable<UpdateFileResponse> {
    this.log('Updating file URL', fileId);
    return this.updateFile(fileId, { urls });
  }

  /**
   * Get all PDF files for an organization
   * 
   * @param orgId - MongoDB ObjectId of the organization
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 10)
   * @returns Observable of PDF files
   * 
   * @example
   * ```typescript
   * this.filesService.getPdfFiles('507f1f77bcf86cd799439011')
   *   .subscribe(response => {
   *     console.log('PDF files:', response.data.files);
   *   });
   * ```
   */
  getPdfFiles(orgId: string, page = 1, limit = 10): Observable<FileListResponse> {
    this.log('Getting PDF files for organization', orgId);
    return this.getFilesByType('application/pdf', { orgId, page, limit });
  }

  /**
   * Get all image files for an organization
   * 
   * @param orgId - MongoDB ObjectId of the organization
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 10)
   * @returns Observable of image files
   * 
   * @example
   * ```typescript
   * this.filesService.getImageFiles('507f1f77bcf86cd799439011')
   *   .subscribe(response => {
   *     console.log('Image files:', response.data.files);
   *   });
   * ```
   */
  getImageFiles(orgId: string, page = 1, limit = 10): Observable<FileListResponse> {
    this.log('Getting image files for organization', orgId);
    return this.getFilesByType('image', { orgId, page, limit });
  }

  /**
   * Get all Word document files for an organization
   * 
   * @param orgId - MongoDB ObjectId of the organization
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 10)
   * @returns Observable of Word document files
   * 
   * @example
   * ```typescript
   * this.filesService.getWordFiles('507f1f77bcf86cd799439011')
   *   .subscribe(response => {
   *     console.log('Word files:', response.data.files);
   *   });
   * ```
   */
  getWordFiles(orgId: string, page = 1, limit = 10): Observable<FileListResponse> {
    this.log('Getting Word files for organization', orgId);
    return this.getFilesByType('application/vnd.openxmlformats-officedocument.wordprocessingml.document', { orgId, page, limit });
  }

  /**
   * Get all Excel spreadsheet files for an organization
   * 
   * @param orgId - MongoDB ObjectId of the organization
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 10)
   * @returns Observable of Excel spreadsheet files
   * 
   * @example
   * ```typescript
   * this.filesService.getExcelFiles('507f1f77bcf86cd799439011')
   *   .subscribe(response => {
   *     console.log('Excel files:', response.data.files);
   *   });
   * ```
   */
  getExcelFiles(orgId: string, page = 1, limit = 10): Observable<FileListResponse> {
    this.log('Getting Excel files for organization', orgId);
    return this.getFilesByType('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', { orgId, page, limit });
  }
}

/**
 * Provider function for UserFiles service
 * 
 * @remarks
 * Use this function to configure the UserFilesService in your application.
 * 
 * @param config - UserFiles service configuration
 * @returns Provider configuration
 * 
 * @example
 * ```typescript
 * import { provideUserFilesService } from 'cms-post-lib';
 * 
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     provideUserFilesService({
 *       apiBaseUrl: environment.apiBaseUrl,
 *       debug: !environment.production
 *     })
 *   ]
 * };
 * ```
 */
export function provideUserFilesService(config: UserFilesConfig) {
  return {
    provide: USER_FILES_API_CONFIG,
    useValue: config
  };
}
